import { useEffect, useState } from 'react';
import { fetchPatients } from '../../api/commandApi';
import { resolveActiveTenantId } from '../../auth/tenant';

/**
 * Best-effort patient_id → display-name resolution for the Job Center.
 *
 * Job payloads carry only patient ids, which made every card read as an
 * anonymous "Patient 69e70493…" — indistinguishable when several twins build
 * at once. The patient picker list already knows the names (keyed by MRN, which
 * is the patient id here), so borrow it: one cached fetch per session window,
 * shared across all cards. Strictly best-effort — an unconfigured API just
 * leaves the cards showing ids, never an error.
 */
const CACHE_TTL_MS = 60_000;

let cache: Map<string, string> = new Map();
let inflight: Promise<Map<string, string>> | null = null;
let fetchedAt = 0;

async function loadNames(): Promise<Map<string, string>> {
  const now = Date.now();
  if (cache.size > 0 && now - fetchedAt < CACHE_TTL_MS) return cache;
  if (inflight) return inflight;
  inflight = fetchPatients(resolveActiveTenantId())
    .then((rows) => {
      const next = new Map<string, string>();
      for (const row of rows ?? []) {
        const name = (row.name ?? '').trim();
        if (row.mrn && name) next.set(row.mrn, name);
      }
      cache = next;
      fetchedAt = Date.now();
      return next;
    })
    .catch(() => cache)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Test hook: reset the module cache between tests. */
export function _resetPatientNameCacheForTests(): void {
  cache = new Map();
  inflight = null;
  fetchedAt = 0;
}

export function usePatientNames(enabled: boolean): Map<string, string> {
  const [names, setNames] = useState<Map<string, string>>(cache);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void loadNames().then((resolved) => {
      if (!cancelled && resolved.size > 0) setNames(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return names;
}
